"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { FlaskConical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LocalePicker } from "@/components/LocalePicker";

/**
 * STORY-1302 — site-wide footer with virtual-money disclaimer.
 *
 * Shown on every page. The disclaimer mirrors the BetaBanner copy at
 * the top, but with a longer-form explanation and links to the help
 * desk for users who want more context. Removed once Gate B
 * (real-money launch) flips the platform to real cash.
 *
 * STORY-2211 phase 1 (2026-05-03) — per-locale compliance
 * attribution. The Brazilian footer references LGPD (Lei nº
 * 13.709/2018) as the data-protection framework users in BR are
 * covered by. The default English footer uses generic phrasing
 * (no LGPD reference) per the locked jurisdiction-scoping rule
 * (CLAUDE.md 2026-05-03). Future locales (es-MX → LFPDPPP, en-CA /
 * fr-CA → PIPEDA, en-IN → DPDPA) plug in via the same
 * `complianceFrameworkLine(locale)` helper when those markets
 * activate.
 */
export function complianceFrameworkLine(locale: string): string | null {
  if (locale === "pt" || locale === "pt-BR") {
    return "Conformidade LGPD (Lei nº 13.709/2018) — usuários no Brasil.";
  }
  // Future locales hook in here. For unrecognized locales we render
  // no jurisdiction-specific line at all — generic disclaimer text
  // (in `siteFooter.body`) already covers the global default.
  return null;
}

export function SiteFooter() {
  const { t, i18n } = useTranslation();
  const pathname = usePathname();
  // STORY-616 — embed routes are iframe-only; hide chrome.
  if (pathname?.startsWith("/embed")) return null;
  // v2 redesign (2026-05-21) — the v2 surface ships its own
  // <V2Footer />, so suppress the v1 footer to avoid stacking two
  // disclaimer blocks at the bottom of every /v2/* page.
  if (pathname?.startsWith("/v2")) return null;
  if (pathname?.startsWith("/v3")) return null;
  if (pathname?.startsWith("/v4")) return null;
  if (pathname?.startsWith("/v5")) return null;
  // /v6 used to suppress here when it was a demo route with its own
  // inline footer. With v6 promoted to localhost main theme on
  // 2026-05-24, /v6 should render the same chrome as / — SiteFooter
  // is the canonical footer. The inline /v6 footer was removed.
  return (
    <footer
      className="mt-auto border-t bg-[var(--color-bg)]"
      style={{
        borderColor: "var(--color-border)",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-5 py-6 text-[11px] text-[var(--color-text3)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <div
              className="mb-1 rounded-[4px] inline-block px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-[0.4px]"
              style={{
                borderColor: "var(--color-brand)",
                color: "var(--color-brand)",
                background: "var(--color-brand-tint)",
                border: "1px solid var(--color-brand)",
              }}
            >
              <FlaskConical size={11} strokeWidth={2} aria-hidden="true" className="inline mr-1" />
              {t("siteFooter.label")}
            </div>
            <p className="leading-snug">{t("siteFooter.body")}</p>
          </div>
          <nav className="flex items-center gap-4 text-[11px]">
            <Link
              href="/help"
              className="hover:text-[var(--color-text)]"
            >
              {t("siteFooter.helpLink")}
            </Link>
            <Link
              href="/legal/privacy"
              className="hover:text-[var(--color-text)]"
            >
              {t("siteFooter.privacy")}
            </Link>
            <Link
              href="/legal/tos"
              className="hover:text-[var(--color-text)]"
            >
              {t("siteFooter.terms")}
            </Link>
            {/* 2026-05-24 — Polymarket-style discrete region picker in
             * the footer. Auto-detection still happens on first visit
             * via RegionDetector (sets the granbay.region cookie from
             * Cloudflare CF-IPCountry); this picker lets the user
             * override it without burning top-chrome real estate.
             * Replaces the previous SiteHeader / QuickDashboardFullScreen
             * mounts — one canonical entry point everywhere. */}
            <LocalePicker variant="footer" />
          </nav>
        </div>
        <p className="mt-4 text-[10px]">{t("siteFooter.copyright")}</p>
        {/* Per-locale compliance attribution. Renders nothing when
            the user's locale doesn't map to a recognized
            jurisdiction-specific framework, so default English users
            never see "LGPD" or any other framework name they're not
            actually covered by (locked rule, CLAUDE.md 2026-05-03). */}
        {(() => {
          const line = complianceFrameworkLine(i18n.language ?? "en");
          if (!line) return null;
          return (
            <p className="mt-1 text-[10px] text-[var(--color-text3)]">
              {line}
            </p>
          );
        })()}
      </div>
    </footer>
  );
}
